import React, { FC, useEffect, useMemo, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { Option, ComboboxProps, Dropdown } from "@fluentui/react-components";

import { LabeledCombobox } from "../../../../common/components/LabeledCombobox/LabeledCombobox";
import { IRapComponentProperties } from "../../../../platform/Layout";
import { IUhfViewState } from "../../../../pages/Contracts";
import { TestActions } from "../../redux/TestActions";
import { ITestConfiguration } from "../../Contracts";
import { localizedStrings } from "../../../../common/localization/LocalizedStrings";
import { PageActions } from "../../../../platform/components/Page/redux/PageActions";
import { PageFeature } from "../../../../common/Constants";

import * as TestSelectors from "../../redux/TestSelectors";

interface IPartnerSelectorProvidedProps extends IRapComponentProperties {
    className?: string;
}

interface IPartnerSelectorOwnProps {
    testConfig: ITestConfiguration;
}

export type IPartnerSelectorProps = ConnectedProps<typeof connector> & IPartnerSelectorProvidedProps;

const PartnerSelectorInitializer: FC<IPartnerSelectorProps> = (props) => {
    const [partnerList, setPartnerList] = useState<string[]>([]);
    const [partnerListFiltered, setPartnerListFiltered] = useState<string[]>([]); //combobox gets really slow with 1000+ options, need to show only a subset
    const [isLoading, setIsLoading] = useState(false);

    const MaxListLength = 10;

    useEffect(() => {
        setIsLoading(true);
        props.fetchPartnerList().then(partnerList => {
            setPartnerList(partnerList);
            setPartnerListFiltered(partnerList.slice(0, MaxListLength))
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    const onInput = (val: string) => {
        if(!val) {
            setPartnerListFiltered(partnerList?.slice(0, MaxListLength));
            return;
        }

        let valLower = val.toLowerCase();
        let filteredOptions = partnerList.filter(o => {
            return o.toLowerCase().startsWith(valLower);
        });

        setPartnerListFiltered(filteredOptions.slice(0, MaxListLength));
    }
    
    const onOptionSelect: ComboboxProps["onOptionSelect"] = (event, data) => {
        props.logTelemetry(PageFeature.TestPage, "Selected new Partner ID", { partnerId: data.optionText });
        props.setTestConfig({ partnerId: data.optionText, headerId: "", footerId: "" });
    }

    return (
        <div className={`${props.className ? props.className : ""}`}>
            <LabeledCombobox 
                label={localizedStrings.TestView?.TestConfigurator?.partnerId} 
                onOptionSelect={onOptionSelect} 
                value={props.testConfig.partnerId} 
                disabled={isLoading}
                onInput={onInput}
                onInputDebounceTime={300}
                tooltip={"Please type in your Partner ID to filter this list."}
            >
                {partnerListFiltered?.map(opt => {
                    return (
                        <Option key={opt}>
                            {opt}
                        </Option>
                    ); 
                })}
            </LabeledCombobox>
        </div>
    );
}

function mapStateToProps(state: IUhfViewState, providedProps: IPartnerSelectorProvidedProps): Partial<IPartnerSelectorOwnProps> {
    return {
        ...providedProps,
        testConfig: TestSelectors.getTestConfig(state)
    };
}

const ActionsToDispatch = {
    setTestConfig: TestActions.setTestConfig,
    fetchPartnerList: TestActions.fetchPartnerList,
    logTelemetry: PageActions.logTelemetry
};

const connector = connect(
    mapStateToProps,
    ActionsToDispatch,
    null,
    { forwardRef: true }
);

export const PartnerSelector = connector(PartnerSelectorInitializer);